/* eslint-disable @typescript-eslint/member-ordering */
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Pagination } from '../../shared/models/internal/pagination';
import { errorToString } from '../common/common-utils';

@Injectable({
  providedIn: 'root',
})
export class PaginationService<T> {
  private itemsSubject = new BehaviorSubject<T[]>([]);
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  private hasMoreDataSubject = new BehaviorSubject<boolean>(true);
  private totalItemsCount = new BehaviorSubject<number>(0);

  public items$ = this.itemsSubject.asObservable();
  public isLoading$ = this.isLoadingSubject.asObservable();
  public hasMoreData$ = this.hasMoreDataSubject.asObservable();
  public totalItems$ = this.totalItemsCount.asObservable();

  private currentPage = 0;
  private pageSize = 10;

  /**
   * Initializes the pagination service with the specified page size.
   * If no page size is provided, it defaults to 50.
   */
  public initialize(pageSize?: number): void {
    this.currentPage = 0;
    this.pageSize = pageSize ?? 50;
    this.itemsSubject.next([]);
    this.hasMoreDataSubject.next(true);
  }

  public reset(): void {
    this.currentPage = 0;
    this.itemsSubject.next([]);
    this.hasMoreDataSubject.next(true);
    this.totalItemsCount.next(0);
  }

  public loadNextPage(fetchFn: (page: number, pageSize: number) => Observable<Pagination<T>>): Promise<void> {
    if (this.isLoadingSubject.value || !this.hasMoreDataSubject.value) {
      return Promise.resolve();
    }

    this.isLoadingSubject.next(true);

    return new Promise((resolve) => {
      fetchFn(this.currentPage, this.pageSize).subscribe({
        next: (pagination) => {
          if (pagination.items.length < this.pageSize) {
            this.hasMoreDataSubject.next(false);
          }
          const currentItems = this.itemsSubject.value;
          this.totalItemsCount.next(pagination.totalCount);
          this.itemsSubject.next([...currentItems, ...pagination.items]);
          this.currentPage = pagination.currentPage + 1;
        },
        error: (error) => {
          console.error('Error fetching data:', errorToString(error));
          this.isLoadingSubject.next(false);
          resolve();
        },
        complete: () => {
          this.isLoadingSubject.next(false);
          resolve();
        },
      });
    });
  }

  public get currentPageNumber(): number {
    return this.currentPage - 1;
  }
}
